import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  serverTimestamp,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { Bill, BillLogEntry } from '../../types'
import {
  decideBillPayWrite,
  mergeBillLogAppend,
  omitBillLogs,
  preserveBillLogs,
} from '../billLogs'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'
import { businessDayUtcBounds } from '@shared/lib/customerEventDayBounds'
import { businessDayIsoBounds, liveStartIso, monthKeyBounds, nowMs } from './liveCacheWindow'
import {
  LIVE_UPDATED_AT_FIELD,
  mergeByServerUpdateTime,
  versionedRowFromSnap,
  type VersionedRow,
} from './mergeByServerUpdateTime'
import { recordLiveCounts, recordSnapshotApplyMs } from './perfPulse'

export const BILLS_UPDATED_EVENT = 'wortelogy-bills-updated'

const billsCacheByPlace: Record<string, Bill[]> = {}
const historyBillsByPlace: Record<string, Map<string, Bill>> = {}
let billsUnsubscribes: (() => void)[] = []
let liveOpenRows: VersionedRow<Bill>[] | null = null
let liveRecentPaidRows: VersionedRow<Bill>[] | null = null
let subscribedLiveStartIso = ''

function billsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'bills')
}

function billDoc(placeId: string, billId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'bills', billId)
}

function dispatchBillsUpdated(): void {
  window.dispatchEvent(new CustomEvent(BILLS_UPDATED_EVENT))
}

function billFromDoc(id: string, data: Record<string, unknown>): Bill {
  return { id, ...data } as Bill
}

function billFromSnap(d: QueryDocumentSnapshot): VersionedRow<Bill> {
  return versionedRowFromSnap(d, billFromDoc)
}

function mergeBillsById(...lists: Bill[][]): Bill[] {
  const map = new Map<string, Bill>()
  for (const list of lists) {
    for (const bill of list) map.set(bill.id, bill)
  }
  return [...map.values()]
}

function historyMap(placeId: string): Map<string, Bill> {
  if (!historyBillsByPlace[placeId]) historyBillsByPlace[placeId] = new Map()
  return historyBillsByPlace[placeId]
}

function replaceHistoryBills(placeId: string, rows: Bill[]): void {
  const hist = new Map<string, Bill>()
  for (const bill of rows) hist.set(bill.id, bill)
  historyBillsByPlace[placeId] = hist
}

function staffVisibleBill(bill: Bill): boolean {
  return bill.guestPaymentOnly !== true || Boolean(bill.paidAt)
}

function emitLiveBills(placeId: string): void {
  if (liveOpenRows === null || liveRecentPaidRows === null) return
  const started = nowMs()
  const live = mergeByServerUpdateTime(liveOpenRows, liveRecentPaidRows)
  const prevById = new Map((billsCacheByPlace[placeId] ?? []).map((b) => [b.id, b]))
  billsCacheByPlace[placeId] = live.map((bill) => preserveBillLogs(prevById.get(bill.id), bill))
  recordSnapshotApplyMs(nowMs() - started)
  recordLiveCounts({ bills: live.length })
  dispatchBillsUpdated()
}

function applyBillPatchToCache(id: string, patch: Partial<Bill>, log?: BillLogEntry): void {
  const placeId = getCurrentPlaceId()
  const safe = omitBillLogs(patch)
  const apply = (bill: Bill): Bill => {
    const next = { ...bill, ...safe }
    if (log) next.logs = mergeBillLogAppend(next.logs, log)
    return next
  }
  const cache = billsCacheByPlace[placeId] ?? []
  const liveIdx = cache.findIndex((b) => b.id === id)
  if (liveIdx >= 0) {
    billsCacheByPlace[placeId] = cache.map((b) => (b.id === id ? apply(b) : b))
  } else {
    const hist = historyMap(placeId)
    const existing = hist.get(id)
    if (existing) hist.set(id, apply(existing))
  }
  dispatchBillsUpdated()
}

function stopBillListeners(): void {
  for (const unsub of billsUnsubscribes) unsub()
  billsUnsubscribes = []
  liveOpenRows = null
  liveRecentPaidRows = null
}

function bindBillListeners(placeId: string, startIso: string): void {
  const ref = billsRef(placeId)
  billsUnsubscribes.push(
    onSnapshot(
      query(ref, where('paidAt', '==', null)),
      (snapshot) => {
        liveOpenRows = snapshot.docs.map((d) => billFromSnap(d))
        emitLiveBills(placeId)
      },
      (err) => console.error('[bills] open subscribe failed', err),
    ),
    onSnapshot(
      query(ref, where('paidAt', '>=', startIso)),
      (snapshot) => {
        liveRecentPaidRows = snapshot.docs.map((d) => billFromSnap(d))
        emitLiveBills(placeId)
      },
      (err) => console.error('[bills] recent paid subscribe failed', err),
    ),
  )
}

export async function primeBillsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  stopBillListeners()
  const startIso = liveStartIso()
  subscribedLiveStartIso = startIso
  const ref = billsRef(placeId)
  const [openSnap, recentSnap] = await Promise.all([
    getDocs(query(ref, where('paidAt', '==', null))),
    getDocs(query(ref, where('paidAt', '>=', startIso))),
  ])
  liveOpenRows = openSnap.docs.map((d) => billFromSnap(d))
  liveRecentPaidRows = recentSnap.docs.map((d) => billFromSnap(d))
  emitLiveBills(placeId)
  bindBillListeners(placeId, startIso)
}

export function getBills(): Bill[] {
  const placeId = getCurrentPlaceId()
  return (billsCacheByPlace[placeId] ?? []).filter(staffVisibleBill)
}

export function getHistoryBills(): Bill[] {
  const placeId = getCurrentPlaceId()
  return [...(historyBillsByPlace[placeId]?.values() ?? [])].filter(staffVisibleBill)
}

export function getBillsWithHistory(): Bill[] {
  return mergeBillsById(getHistoryBills(), getBills())
}

export function getBill(id: string): Bill | undefined {
  const placeId = getCurrentPlaceId()
  const cache = billsCacheByPlace[placeId] ?? []
  const live = cache.find((b) => b.id === id)
  if (live && staffVisibleBill(live)) return live
  const history = historyBillsByPlace[placeId]?.get(id)
  if (history && staffVisibleBill(history)) return history
  return undefined
}

/** Upsert the given bills only. Never deletes docs missing from a scoped cache. */
export function saveBills(bills: Bill[]): void {
  const placeId = getCurrentPlaceId()
  const prev = billsCacheByPlace[placeId] ?? []
  const byId = new Map(prev.map((b) => [b.id, b]))
  for (const bill of bills) {
    const withoutLogs = omitBillLogs(bill) as Bill
    byId.set(bill.id, preserveBillLogs(byId.get(bill.id), withoutLogs))
  }
  billsCacheByPlace[placeId] = [...byId.values()]
  dispatchBillsUpdated()
  ;(async () => {
    for (const bill of bills) {
      const sanitized = stripUndefined(omitBillLogs(bill))
      await setDoc(
        billDoc(placeId, bill.id),
        { ...sanitized, [LIVE_UPDATED_AT_FIELD]: serverTimestamp() },
        { merge: true },
      )
    }
  })().catch((e) => console.error('Firestore saveBills failed', e))
}

export function generateBillId(): string {
  const now = new Date()
  const date = now.toISOString().slice(0, 10).replace(/-/g, '')
  const time = now.toTimeString().slice(0, 8).replace(/:/g, '')
  return `BILL-${date}-${time}`
}

export function addBill(bill: Omit<Bill, 'id'>, predeterminedId?: string): Bill {
  const placeId = getCurrentPlaceId()
  const id = predeterminedId ?? generateBillId()
  const full: Bill = stripUndefined({ ...bill, id })
  const cache = billsCacheByPlace[placeId] ?? []
  billsCacheByPlace[placeId] = [...cache, full]
  setDoc(billDoc(placeId, id), { ...full, [LIVE_UPDATED_AT_FIELD]: serverTimestamp() }).catch((e) =>
    console.error('Firestore addBill failed', e),
  )
  dispatchBillsUpdated()
  return full
}

export function updateBill(id: string, patch: Partial<Bill>): Bill[] {
  const placeId = getCurrentPlaceId()
  const safe = omitBillLogs(patch)
  applyBillPatchToCache(id, safe)
  const found = getBill(id)
  if (found) {
    const sanitized = stripUndefined(safe)
    setDoc(
      billDoc(placeId, id),
      { ...sanitized, [LIVE_UPDATED_AT_FIELD]: serverTimestamp() },
      { merge: true },
    ).catch((e) => console.error('Firestore updateBill failed', e))
  }
  return billsCacheByPlace[placeId]
}

export async function appendBillLogEntry(id: string, entry: BillLogEntry): Promise<number> {
  const placeId = getCurrentPlaceId()
  applyBillPatchToCache(id, {}, entry)
  const started =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()
  try {
    await updateDoc(billDoc(placeId, id), {
      logs: arrayUnion(stripUndefined(entry)),
      [LIVE_UPDATED_AT_FIELD]: serverTimestamp(),
    })
  } catch (e) {
    console.error('Firestore appendBillLogEntry failed', e)
  }
  const ended =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()
  return Math.max(0, Math.round(ended - started))
}

export type CommitBillPaymentResult =
  | { status: 'committed' }
  | { status: 'rejected'; paidAt: string }
  | { status: 'failed'; error: string }

export async function commitBillPaymentTransaction(
  id: string,
  patch: Partial<Bill>,
  logEntry: BillLogEntry,
): Promise<CommitBillPaymentResult> {
  const placeId = getCurrentPlaceId()
  const db = getFirestoreInstance()
  try {
    const decision = await runTransaction(db, async (tx) => {
      const ref = billDoc(placeId, id)
      const snap = await tx.get(ref)
      const planned = decideBillPayWrite({ exists: snap.exists(), paidAt: snap.data()?.paidAt })
      if (planned.action === 'commit') {
        tx.update(ref, {
          ...stripUndefined(omitBillLogs(patch)),
          logs: arrayUnion(stripUndefined(logEntry)),
          [LIVE_UPDATED_AT_FIELD]: serverTimestamp(),
        })
      }
      return planned
    })
    if (decision.action === 'commit') {
      applyBillPatchToCache(id, patch, logEntry)
      return { status: 'committed' }
    }
    if (decision.action === 'reject') {
      return { status: 'rejected', paidAt: decision.paidAt }
    }
    return { status: 'failed', error: 'Bill not found' }
  } catch (e) {
    return { status: 'failed', error: e instanceof Error ? e.message : String(e) }
  }
}

export async function fetchBillsForIsoRange(startIso: string, endIso: string): Promise<Bill[]> {
  const placeId = getCurrentPlaceId()
  const ref = billsRef(placeId)
  const [createdSnap, paidSnap] = await Promise.all([
    getDocs(query(ref, where('createdAt', '>=', startIso), where('createdAt', '<', endIso))),
    getDocs(query(ref, where('paidAt', '>=', startIso), where('paidAt', '<', endIso))),
  ])
  const rows = mergeBillsById(
    createdSnap.docs.map((d) => billFromDoc(d.id, d.data() as Record<string, unknown>)),
    paidSnap.docs.map((d) => billFromDoc(d.id, d.data() as Record<string, unknown>)),
  )
  return rows.filter(staffVisibleBill)
}

export function setHistoryBills(rows: Bill[]): void {
  replaceHistoryBills(getCurrentPlaceId(), rows)
  dispatchBillsUpdated()
}

const EXPORT_BILL_TIME_FIELDS = ['createdAt', 'paidAt', 'cancelledAt', 'refundedAt', 'disputedAt'] as const

/** Bills created, paid, cancelled, refunded, or disputed inside the business-date range. Does not touch the history overlay. */
export async function fetchBillsForExportRange(
  startDate: string,
  endDate: string,
  timezone: string,
): Promise<Bill[]> {
  if (!startDate || !endDate || startDate > endDate) return []
  const placeId = getCurrentPlaceId()
  const ref = billsRef(placeId)
  const startIso = businessDayUtcBounds(startDate, timezone).startIso
  const endIso = businessDayUtcBounds(endDate, timezone).endIso
  const snaps = await Promise.all(
    EXPORT_BILL_TIME_FIELDS.map((field) =>
      getDocs(query(ref, where(field, '>=', startIso), where(field, '<', endIso))),
    ),
  )
  const rows = mergeBillsById(
    ...snaps.map((snap) =>
      snap.docs.map((d) => billFromDoc(d.id, d.data() as Record<string, unknown>)),
    ),
  )
  return rows.filter(staffVisibleBill)
}

export async function fetchBillsForBusinessDay(date: string): Promise<Bill[]> {
  const { startIso, endIso } = businessDayIsoBounds(date)
  return fetchBillsForIsoRange(startIso, endIso)
}

export async function fetchBillsForMonth(monthKey: string): Promise<Bill[]> {
  const { startIso, endIso } = monthKeyBounds(monthKey)
  return fetchBillsForIsoRange(startIso, endIso)
}

export function dropBillHistoryOverlays(): void {
  const placeId = getCurrentPlaceId()
  if (!historyBillsByPlace[placeId] || historyBillsByPlace[placeId].size === 0) return
  historyBillsByPlace[placeId] = new Map()
  dispatchBillsUpdated()
}

export function isBillsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in billsCacheByPlace
}

export function clearBillsCache(): void {
  stopBillListeners()
  subscribedLiveStartIso = ''
  Object.keys(billsCacheByPlace).forEach((k) => delete billsCacheByPlace[k])
  Object.keys(historyBillsByPlace).forEach((k) => delete historyBillsByPlace[k])
}

export function subscribedBillsLiveStartIso(): string {
  return subscribedLiveStartIso
}
